import { isTestEnv } from '@app/helpers/env';
import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

export interface ContentRect {
  width: number;
  height: number;
  top: number;
  right: number;
  left: number;
  bottom: number;
}

/** Stable fake rect in test runs so layout-dependent UI gets a non-zero width without ResizeObserver. */
const TEST_CONTENT_RECT: ContentRect = {
  width: 1200,
  height: 600,
  top: 0,
  right: 0,
  left: 1200,
  bottom: 600,
};

/**
 * Subscribes a ResizeObserver to `ref.current` and returns the latest `contentRect`.
 * Re-attaches when the ref target changes (e.g. child mounts after `ref` was null on first paint).
 * A stable `ref` object alone does not trigger re-observe — the previous `useEffect([ref])` pattern
 * missed late-mounted nodes and left width/height at 0 forever.
 */
export function useMeasureDirty(ref: RefObject<HTMLElement>): ContentRect {
  const frame = useRef(0);
  const lastObserved = useRef<HTMLElement | null>(null);
  const [rect, set] = useState({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  });

  const [observer] = useState(
    () =>
      new ResizeObserver((entries) => {
        const entry = entries[0];

        if (entry) {
          cancelAnimationFrame(frame.current);

          frame.current = requestAnimationFrame(() => {
            if (ref.current) {
              set(entry.contentRect);
            }
          });
        }
      }),
  );

  // Intentionally run after every layout: `ref` identity is stable, but `ref.current` can attach later.
  useLayoutEffect(() => {
    if (isTestEnv()) {
      return;
    }
    const el = ref.current;
    if (el === lastObserved.current) {
      return;
    }
    observer.disconnect();
    lastObserved.current = el;
    if (el) {
      observer.observe(el);
    }
  });

  return isTestEnv() ? TEST_CONTENT_RECT : rect;
}
